import React from "react";
import { Card, Table, Row, Col } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

const TopTable = () => {
    const { t } = useTranslation();
    const columns = [
        {
            title: t('customer_report.purchase_cycle'),
            dataIndex: "cycle",
            key: "cycle",
            fixed: "left" as const,
            width: 220,
        },
        {
            title: t('customer_report.days_not_bought_again'),
            children: [
                {
                    title: t('customer_report.1_30_days'),
                    dataIndex: "d1_30",
                    align: "center" as const,
                },
                {
                    title: t('customer_report.31_60_days'),
                    dataIndex: "d31_60",
                    align: "center" as const,
                },
                {
                    title: t('customer_report.61_90_days'),
                    dataIndex: "d61_90",
                    align: "center" as const,
                },
                {
                    title: t('customer_report.gte_91_days'),
                    dataIndex: "d91",
                    align: "center" as const,
                },
            ],
        },
        {
            title: t('customer_report.total'),
            dataIndex: "total",
            align: "center" as const,
        },
        {
            title: t('customer_report.rate_by_cycle'),
            dataIndex: "rate",
            align: "center" as const,
            width: 220,
        },
    ];

    const data = [
        {
            key: 1,
            cycle: t('customer_report.customers_not_bought_again'),
            d1_30: 0,
            d31_60: 0,
            d61_90: 0,
            d91: 0,
            total: 0,
            rate: "0%",
        },
        {
            key: 2,
            cycle: t('customer_report.1_30_days'),
            d1_30: 0,
            d31_60: 0,
            d61_90: 0,
            d91: 0,
            total: 0,
            rate: "0%",
        },
        {
            key: 3,
            cycle: t('customer_report.31_60_days'),
            d1_30: 0,
            d31_60: 0,
            d61_90: 0,
            d91: 0,
            total: 0,
            rate: "0%",
        },
        {
            key: 4,
            cycle: t('customer_report.61_90_days'),
            d1_30: 0,
            d31_60: 0,
            d61_90: 0,
            d91: 0,
            total: 0,
            rate: "0%",
        },
        {
            key: 5,
            cycle: t('customer_report.gte_91_days'),
            d1_30: 0,
            d31_60: 0,
            d61_90: 0,
            d91: 0,
            total: 0,
            rate: "0%",
        },
        {
            key: 6,
            cycle: t('customer_report.total'),
            d1_30: 0,
            d31_60: 0,
            d61_90: 0,
            d91: 0,
            total: 0,
            rate: "0%",
            isTotal: true,
        },
        {
            key: 7,
            cycle: t('customer_report.rate_by_time_not_bought'),
            d1_30: "0%",
            d31_60: "0%",
            d61_90: "0%",
            d91: "0%",
            total: "100%",
            rate: "",
            isHighlight: "yellow",
        },
        {
            key: 8,
            cycle: t('customer_report.customers_may_leave'),
            d1_30: 0,
            d31_60: 0,
            d61_90: 0,
            d91: 0,
            total: "0 (0%)",
            rate: "",
            isHighlight: "red",
        },
    ];

    return (
        <Card
            title={t('customer_report.days_not_repurchased')}
            className="shadow-sm"
            extra={
                <div className="flex items-center gap-1 cursor-pointer text-blue-500 hover:text-blue-600">
                    <DownloadOutlined />
                    <span>{t('customer_report.download_excel')}</span>
                </div>
            }
        >
            <Table
                columns={columns}
                dataSource={data}
                pagination={false}
                bordered
                scroll={{ x: "max-content" }}
                rowClassName={(record) => {
                    if (record.isTotal) return "!bg-gray-200 !font-semibold";
                    if (record.isHighlight === "yellow") return "!bg-yellow-100";
                    if (record.isHighlight === "red") return "!bg-red-100";
                    return "";
                }}
            />
        </Card>
    );
};

const NewCustomerTable = () => {
    const { t } = useTranslation();
    const columns = [
        { title: t('customer_report.customer'), dataIndex: "name" },
        { title: t('customer_report.quantity'), dataIndex: "count" },
        { title: t('customer_report.ratio'), dataIndex: "rate" },
    ];

    const data = [
        {
            key: 1,
            name: t('customer_report.new_start_buying'),
            count: 10,
            rate: "50%",
        },
        {
            key: 2,
            name: t('customer_report.buy_create_card'),
            count: 5,
            rate: "25%",
        },
        {
            key: 3,
            name: t('customer_report.buy_once'),
            count: 5,
            rate: "25%",
        },
    ];

    return (
        <Card
            className="h-full flex flex-col"
            title={t('customer_report.new_customer_analysis')}
            extra={
                <div className="flex items-center gap-1 cursor-pointer text-blue-500 hover:text-blue-600">
                    <DownloadOutlined />
                    <span>{t('customer_report.download_excel')}</span>
                </div>
            }
        >
            <Table
                columns={columns}
                dataSource={data}
                pagination={false}
                size="small"
                className="custom-table-300 custom-ant-table"
                scroll={{ x: "max-content" }}
            />
        </Card>
    );
};

const BoughtCustomerTable = () => {
    const { t } = useTranslation();
    const columns = [
        { title: t('customer_report.purchase_times'), dataIndex: "times" },
        { title: t('customer_report.customer_count_short'), dataIndex: "customers" },
        { title: t('customer_report.ratio'), dataIndex: "rate" },
        { title: t('customer_report.revenue'), dataIndex: "revenue" },
    ];

    const data = [
        { key: 1, times: 1, customers: 10, rate: "40%", revenue: "5,000,000đ" },
        { key: 2, times: 2, customers: 8, rate: "30%", revenue: "4,000,000đ" },
        { key: 3, times: 3, customers: 5, rate: "20%", revenue: "2,000,000đ" },
        { key: 4, times: ">4", customers: 2, rate: "10%", revenue: "1,000,000đ" },
    ];

    return (
        <Card
            className="h-full flex flex-col"
            title={t('customer_report.bought_customer_analysis')}
            extra={
                <div className="flex items-center gap-1 cursor-pointer text-blue-500 hover:text-blue-600">
                    <DownloadOutlined />
                    <span>{t('customer_report.download_excel')}</span>
                </div>
            }
        >
            <Table
                columns={columns}
                dataSource={data}
                pagination={false}
                size="small"
                className="custom-table-300 custom-ant-table"
                scroll={{ x: "max-content" }}
            />
        </Card>
    );
};

const CustomerList = () => {
    const { t } = useTranslation();
    const columns = [
        { title: t('customer_report.customer_name'), dataIndex: "name" },
        { title: t('customer_report.phone'), dataIndex: "phone" },
        { title: t('customer_report.order_count'), dataIndex: "orders" },
        { title: t('customer_report.success_orders'), dataIndex: "success" },
        { title: t('customer_report.return_orders'), dataIndex: "return" },
        { title: t('customer_report.total_spent'), dataIndex: "spent" },
    ];

    const data = [
        {
            key: 1,
            name: "Nguyễn Văn A",
            phone: "0901234567",
            orders: 5,
            success: 4,
            return: 1,
            spent: "2,000,000đ",
        },
        {
            key: 2,
            name: "Trần Thị B",
            phone: "0912345678",
            orders: 3,
            success: 3,
            return: 0,
            spent: "1,200,000đ",
        },
    ];

    return (
        <Card
            title={t('customer_report.customer_list')}
            extra={
                <div className="flex items-center gap-1 cursor-pointer text-blue-500 hover:text-blue-600">
                    <DownloadOutlined />
                    <span>{t('customer_report.download_excel')}</span>
                </div>
            }
        >
            <Table
                columns={columns}
                dataSource={data}
                className="custom-ant-table"
                scroll={{ x: "max-content" }}
                pagination={false}
                size="small"
            />
        </Card>
    );
};

export default function TablePage() {
    return (
        <div className="space-y-4">
            <TopTable />

            <Row gutter={[16, 16]} className="items-stretch">
                <Col xs={24} sm={24} md={12} className="flex">
                    <div className="flex-1">
                        <NewCustomerTable />
                    </div>
                </Col>

                <Col xs={24} sm={24} md={12} className="flex">
                    <div className="flex-1">
                        <BoughtCustomerTable />
                    </div>
                </Col>
            </Row>

            <CustomerList />
        </div>
    );
}